/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import Request, {ANSMetadata, Metadata} from './model/Request';
import Response, {ANSRecord, ANSResponse} from './model/Response';
import Utils from './Utils';
import MessageMapping, {ANSTypeSubType} from './model/MessageType';
import ProductMapping, {defaultProductName} from './model/ProductMapping';
import Configuration, {AuthInfo} from './model/Configuration';
import PulseANSConfig from './config/PulseANSConfig.json';
import {PulseService} from './PulseService';

const SUCCESS_RESPONSE_CODE = 201;
const FAILURE_RESPONSE_CODE = 400;
const DEFAULT_TYPE = 'com.adobe.exc.v1.type';
const DEFAULT_SUBTYPE = 'com.adobe.exc.v1.subtype';

export default class PulseANSService extends PulseService {
  public pulseEndpoint: string;
  public authInfo: AuthInfo;

  /**
   * @description Constructor for UserService class.
   *
   * @param {Object} configuration -  Config to initialize Pulse service.
   */
  public constructor(configuration: Configuration) {
    super();
    this.authInfo = configuration.authInfo;
    this.pulseEndpoint = PulseANSConfig.endpoints[configuration.env];
  }

  public async sendNotifications(request: Request): Promise<Response> {
    const that = this;
    const {error, opts, url} = that.processParams(request);
    const returnResponse: Response = {notifications: {notification: []}};

    if (error) {
      returnResponse.notifications.notification.push({
        statusCode: FAILURE_RESPONSE_CODE,
        errorMessage: error,
        'notification-id': ''
      });
      return new Promise(async resolve => {
        resolve(returnResponse);
      });
    }

    const response = await fetch(url, opts);
    return new Promise(async resolve => {
      const returnElement: any = {};

      try {
        const ansResponse: ANSResponse = (await response.json()).notifications;
        ansResponse.notification.forEach((element: ANSRecord) => {
          if (element.errorCode) {
            returnElement.statusCode = element.errorCode;
            returnElement.errorMessage = element.errorMessage;
            returnElement['notification-id'] = '';
          } else {
            returnElement.statusCode = SUCCESS_RESPONSE_CODE;
            returnElement.errorMessage = '';
            returnElement['notification-id'] = element['notification-id'];
          }
          returnResponse.notifications.notification.push(returnElement);
        });
        resolve(returnResponse);
      } catch (error) {
        returnElement.statusCode = FAILURE_RESPONSE_CODE;
        returnElement.errorMessage = error;
        returnElement['notification-id'] = '';
        returnResponse.notifications.notification.push(returnElement);
        resolve(returnResponse);
      }
    });
  }

  private processParams({data, userProfile, imsOrgId, method, appId}: Request): any {
    const {error} = Utils.validateBaseParams(data, imsOrgId, method);

    if (error) {
      return {error, opts: {}, url: ''};
    }

    const notifications = [];
    for (const element of data) {
      // Validate ANS Request and fail fast
      const {error} = Utils.validateANSRequest(element, method);
      if (error) {
        return {error, opts: {}, url: ''};
      }
      let typeSubType: ANSTypeSubType | undefined = MessageMapping.get(element.messageType);
      if (!typeSubType) {
        typeSubType = {type: DEFAULT_TYPE, subtype: DEFAULT_SUBTYPE};
      }
      element.templateParams.user = Utils.constructUserInfo(userProfile);
      let metadata: Metadata | undefined = element.metadata;
      if (!metadata) {
        metadata = {};
      }
      let productName: string | undefined = ProductMapping.get(appId.toLowerCase());
      if (!productName) {
        productName = defaultProductName;
      }
      metadata['product'] = productName;
      const ansMetadata: ANSMetadata = Object.assign(Utils.constructMetadata(metadata));
      notifications.push({
        type: typeSubType.type,
        'sub-type': typeSubType.subtype,
        'user-id': element.userIds,
        payload: JSON.stringify(element.templateParams),
        'ims-org': imsOrgId,
        metadata: ansMetadata
      });
    }
    const opts: RequestInit = Object.assign({
      headers: Utils.getANSDefaultHeaders(this.authInfo.accessToken),
      method,
      body: JSON.stringify({
        notifications: {
          notification: notifications
        }
      })
    });
    const url = new URL(this.pulseEndpoint + '/ans/v1/notifications');
    return {opts, url: url.href};
  }
}
